import React, {useEffect, useState} from 'react';
import MainLayout from '../../../../mainLayout';
import styled, {css, useTheme} from 'styled-components/native';
import {tr} from '../../../../../translations';
import {Theme} from '../../../../../theme/lightTheme';
import TextGeneric from '../../../../../components/textGeneric';
import {isWeb} from '../../../../../utils/responsive';
import {Alert, TouchableOpacity} from 'react-native';
import {getFontFamily} from '../../../../../utils/helpers';
import Icon from '../../../../../components/icon';
import {generatePdf} from '../../../../../utils/pdf';
import {ViewPatientReportProps} from '../../../../../interfaces/screens/viewPatientReport';
import {
  FormField,
  PatientReportType,
} from '../../../../../interfaces/api/content/ehr';
import {deletePatientReport, getPatientReport} from '../../patients/actions';
import TableRowSkeleton from '../../../../../components/tableRowSkeleton';
import EmptyList from '../../../../../components/emptyList';
import screens from '../../../../../config/screens';
import {openQuestionModal} from '../../../../../connected-components/questionModal/actions';
import {NavigationProp, useNavigation} from '@react-navigation/native';
import dayjs from 'dayjs';
import useResponsiveDimensions from '../../../../../utils/hooks/useResponsiveDimensions';
import {showGlobalToast} from '../../../../../connected-components/toast/actions';
import {ToastType} from '../../../../../utils/enums';

const EmptyContainer = styled.View`
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: ${({theme}) => theme.text.s55 * 2}px;
`;

const Container = styled.View`
  width: 100%;
  flex: 1;
  position: relative;
  padding-top: ${({theme}) => theme.text.s55 * 2}px;
  background-color: ${({theme}) =>
    theme.screenBackground === '#ffffff' ? '#f5f7fa' : theme.screenBackground};
`;

const ContentContainer = styled.View`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding-horizontal: ${({theme}) => theme.text.s55}px;
`;

const ReportCardContainer = styled.View<{isMobile?: boolean}>`
  width: 100%;
  background-color: ${({theme}) => theme.screenBackground};
  border-radius: ${({isMobile}) => (isMobile ? '8px' : '12px')};
  margin-bottom: ${({isMobile}) => (isMobile ? '20px' : '30px')};
  shadow-opacity: 0.15;
  shadow-radius: 12px;
  padding: ${({isMobile, theme}) =>
    isMobile ? `${theme.text.s55}px` : `${theme.text.s55 * 1.5}px`};
  shadow-color: ${({theme}) => theme.text.black};
  shadow-offset: 0px 4px;
  elevation: 4;
  border: 1px solid ${({theme}) => theme.border};
`;

const HeaderContainer = styled.View<{isMobile: boolean}>`
  flex-direction: ${({isMobile}) => (isMobile ? 'column' : 'row')};
  justify-content: space-between;
  align-items: ${({isMobile}) => (isMobile ? 'flex-start' : 'center')};
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom-width: 1px;
  border-bottom-color: ${({theme}) => theme.border};
`;

const ReportTitle = styled(TextGeneric)<{isMobile: boolean}>`
  font-size: ${({isMobile}) => (isMobile ? '22px' : '28px')};
  color: ${({theme}) => theme.text.title};
  padding-right: 5%;
  margin-bottom: ${({isMobile}) => (isMobile ? '15px' : '0')};
`;

const InfoSection = styled.View<{isMobile?: boolean}>`
  margin-bottom: ${({isMobile}) => (isMobile ? '15px' : '20px')};
  padding: ${({isMobile}) => (isMobile ? '8px' : '10px')};
  border-radius: 8px;
  background-color: ${({theme}) =>
    theme.screenBackground === '#ffffff'
      ? '#f8f9fb'
      : 'rgba(255,255,255,0.05)'};
`;

const InfoLabel = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.text.dark};
  margin-bottom: 6px;
  ${isWeb
    ? css`
        font-weight: bold;
      `
    : css`
        font-family: ${getFontFamily().bold};
      `}
`;

const InfoValue = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.dark};
`;

const SectionTitle = styled(TextGeneric)`
  font-size: 22px;
  color: ${({theme}) => theme.text.title};
  margin-top: 20px;
  margin-bottom: 16px;
  ${isWeb
    ? css`
        font-weight: bold;
      `
    : css`
        font-family: ${getFontFamily().bold};
      `}
`;

const FieldContainer = styled.View`
  margin-bottom: 20px;
  padding: 16px;
  padding-bottom: 12px;
  border-bottom-width: 1px;
  border-bottom-color: ${({theme}) => theme.border};
  background-color: ${({theme}) =>
    theme.screenBackground === '#ffffff'
      ? '#f8f9fb'
      : 'rgba(255,255,255,0.05)'};
  border-radius: 8px;
`;

const ActionContainer = styled.View<{isMobile: boolean}>`
  flex-direction: row;
  justify-content: ${({isMobile}) => (isMobile ? 'flex-start' : 'flex-end')};
  align-items: center;
  width: ${({isMobile}) => (isMobile ? '100%' : 'auto')};
`;

const ActionButton = styled(TouchableOpacity)`
  margin-horizontal: ${({theme}) => theme.text.s55 / 2}px;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background-color: ${({theme}) =>
    theme.screenBackground === '#ffffff' ? '#f0f2f5' : 'rgba(255,255,255,0.1)'};
`;

const ExportButton = styled(TouchableOpacity)<{isMobile?: boolean}>`
  background-color: ${({theme}) => theme.primary + '22'};
  padding: ${({isMobile}) => (isMobile ? '6px 12px' : '8px 16px')};
  border-radius: 6px;
  flex-direction: row;
  align-items: center;
  margin-right: ${({isMobile}) => (isMobile ? '8px' : '12px')};
`;

const ButtonText = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  margin-left: 8px;
  font-size: ${({theme}) => theme.text.s6}px;
  ${isWeb
    ? css`
        font-weight: 500;
      `
    : css`
        font-family: ${getFontFamily().bold};
      `}
`;

/**
 * ViewPatientReport screen component.
 *
 * @param {any} props - The screen component props.
 * @param {object} props.navigation - The navigation object.
 */
const ViewPatientReport: React.FC<ViewPatientReportProps> = ({route}) => {
  const theme = useTheme() as Theme;
  const navigation = useNavigation<NavigationProp<any>>();
  const {isMobile} = useResponsiveDimensions();
  const {report, callback} = route?.params || {};
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState<PatientReportType>();

  const initPage = async () => {
    if (!report?.id) {
      setIsError(true);
      return;
    }
    setIsLoading(true);
    setIsError(false);
    const res = await getPatientReport(report?.id);
    if (res) {
      setData(res);
    }
    setIsLoading(false);
    if (!res) {
    }
  };

  useEffect(() => {
    initPage();
  }, []);

  // Function to handle editing the report
  const handleEditReport = () => {
    navigation.navigate(screens.content.addEditPatientReport, {
      report,
      callback: () => {
        initPage();
        callback?.();
      },
    });
  };

  // Function to handle deleting the report
  const handleDeleteReport = () => {
    if (!report?.id) {
      return;
    }
    openQuestionModal({
      title: 'Delete Patient Report',
      message:
        "Are you sure you want to delete this patient's report? This action cannot be undone.",
      type: 'question',
      onConfirm: async () => {
        const res = await deletePatientReport(report?.id);
        if (res) {
          callback?.();
          navigation.goBack();
        }
      },
    });
  };

  // Function to handle exporting to PDF
  const handleExportPdf = async () => {
    try {
      const fileName = `Patient_Report_${report?.id}`;

      // Generate the HTML content for the PDF
      const htmlContent = generateReportHtml();

      // Generate PDF
      const result = await generatePdf({
        html: htmlContent,
        fileName: fileName,
        padding: 30,
      });

      // Show success message
      showGlobalToast({
        title: 'PDF Export Successful',
        message: `The PDF has been saved to: ${result.filePath}`,
        type: ToastType.HELP,
      });
    } catch (error) {
      Alert.alert('Error', 'Failed to export PDF.');
    }
  };

  // Generate HTML content for the PDF
  const generateReportHtml = () => {
    if (!data?.form_fields?.length) return '';

    const groupedFields = data.form_fields.reduce(
      (acc, field) => {
        const section = field.form_field.section;
        if (!acc[section]) {
          acc[section] = [];
        }
        acc[section].push(field);
        return acc;
      },
      {} as Record<string, FormField[]>,
    );

    // Format sections for the PDF
    const patientInfoSection = `
      <div class="section">
        <h1>${data?.form_label}</h1>
        <div class="info-row">
          <div class="info-label">Patient Name:</div>
          <div class="info-value">${data?.patient.name}</div>
        </div>
        <div class="info-row">
          <div class="info-label">Date:</div>
          <div class="info-value">${dayjs(data?.date).format('YYYY-MM-DD')}</div>
        </div>
        ${
          data?.notes
            ? `
        <div class="info-row">
          <div class="info-label">Notes:</div>
          <div class="info-value">${data?.notes}</div>
        </div>
        `
            : ''
        }
      </div>
      <div class="page-break"></div>
    `;

    // Create HTML sections for each form section
    const formSections = Object.entries(groupedFields || {})
      .map(([section, fields], sectionIndex) => {
        const fieldsHtml = fields
          .map((field, fieldIndex) => {
            const value =
              field.form_field.type === 'multiple_choice'
                ? getMultipleValueLabels(field)
                : getValueLabel(field);

            // Add alternating background for better readability
            const bgClass = fieldIndex % 2 === 0 ? '' : 'alt-bg';

            return `
          <div class="field-container ${bgClass}">
            <div class="field-label">${field.form_field.label}</div>
            <div class="field-value">${value}</div>
            ${
              field.form_field.help_text
                ? `
              <div class="field-help">${field.form_field.help_text}</div>
            `
                : ''
            }
          </div>
        `;
          })
          .join('');

        // Only add page break if not the last section
        const needsPageBreak =
          sectionIndex < Object.entries(groupedFields || {}).length - 1;

        return `
        <div class="section">
          <h2>${section}</h2>
          ${fieldsHtml}
        </div>
        ${needsPageBreak ? '<div class="page-break"></div>' : ''}
      `;
      })
      .join('');

    // Combine all parts with styling - always use light theme colors for PDF
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Patient Report</title>
        <style>
          body {
            font-family: Arial, Helvetica, sans-serif;
            color: #333;
            line-height: 1.5;
            margin: 0;
            padding: 0;
            background-color: #ffffff;
          }
          .section {
            margin-bottom: 30px;
            min-height: 90vh;
            display: flex;
            flex-direction: column;
            padding: 15px;
            position: relative;
          }
          h1 {
            color: #0066cc;
            font-size: 26px;
            margin-bottom: 20px;
            border-bottom: 2px solid #0066cc;
            padding-bottom: 10px;
          }
          h2 {
            color: #0066cc;
            font-size: 22px;
            margin-bottom: 15px;
            border-bottom: 1px solid #0066cc;
            padding-bottom: 8px;
          }
          .info-row {
            display: flex;
            margin-bottom: 10px;
            padding: 10px 15px;
            border-radius: 4px;
            background-color: #f0f7ff;
            border-left: 3px solid #0066cc;
          }
          .info-label {
            font-weight: bold;
            width: 150px;
            color: #333;
          }
          .info-value {
            flex: 1;
          }
          .field-container {
            margin-bottom: 15px;
            padding: 12px 15px;
            border-radius: 6px;
            border: 1px solid #e0e0e0;
            background-color: #f8f9fa;
            box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          }
          .field-container.alt-bg {
            background-color: #f0f7ff;
          }
          .field-label {
            font-weight: bold;
            margin-bottom: 8px;
            color: #0066cc;
            font-size: 15px;
            border-bottom: 1px dashed #e0e0e0;
            padding-bottom: 5px;
          }
          .field-value {
            margin-bottom: 6px;
            color: #333;
            padding-left: 5px;
          }
          .field-help {
            font-style: italic;
            font-size: 12px;
            color: #666;
            padding-top: 5px;
            border-top: 1px dashed #e0e0e0;
            margin-top: 5px;
            background-color: #fffde7;
            padding: 5px;
            border-radius: 3px;
          }
          .page-break {
            page-break-after: always;
            height: 0;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
          }
          .logo {
            max-width: 200px;
            margin: 0 auto;
            display: block;
          }
          .watermark {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            opacity: 0.05;
            z-index: -1;
            pointer-events: none;
            background-repeat: no-repeat;
            background-position: center;
            background-size: 60%;
          }
          .report-date {
            text-align: right;
            margin-bottom: 20px;
            font-size: 14px;
            color: #666;
          }
          .medical-border {
            border-top: 10px solid #0066cc;
            border-bottom: 5px solid #0066cc;
            padding: 2px 0;
            margin-bottom: 20px;
          }
          @media print {
            body {
              background-color: white;
            }
            .section {
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        ${patientInfoSection}
        ${formSections}
      </body>
      </html>
    `;
  };

  // Function to get label for field value (for single choice fields)
  const getValueLabel = (field: FormField) => {
    if (field.form_field.type === 'single_choice' && field.form_field.options) {
      const option = field.form_field.options.find(
        opt => opt.value === field.value,
      );
      return option ? option.label : field.value;
    }
    return field.value;
  };

  // Function to get labels for multiple choice field values
  const getMultipleValueLabels = (field: FormField) => {
    if (
      field.form_field.type === 'multiple_choice' &&
      field.form_field.options &&
      Array.isArray(field.value)
    ) {
      return field.value
        .map(val => {
          const option = field.form_field.options?.find(
            opt => opt.value === val,
          );
          return option ? option.label : val;
        })
        .join(', ');
    }
    return Array.isArray(field.value) ? field.value.join(', ') : field.value;
  };

  // Render patient info section
  const renderPatientInfo = () => (
    <ReportCardContainer isMobile={isMobile}>
      <HeaderContainer isMobile={isMobile}>
        <ReportTitle bold isMobile={isMobile}>
          {data?.form_label}
        </ReportTitle>
        <ActionContainer isMobile={isMobile}>
          <ExportButton onPress={handleExportPdf} isMobile={isMobile}>
            <Icon
              name="download"
              size={isMobile ? theme.text.s7 : theme.text.s6}
              color={theme.text.dark}
            />
            <ButtonText>Export PDF</ButtonText>
          </ExportButton>
          <ActionButton onPress={handleEditReport}>
            <Icon
              name="edit"
              size={isMobile ? theme.text.s6 : theme.text.s55}
              color={theme.settings.icon}
            />
          </ActionButton>
          <ActionButton onPress={handleDeleteReport}>
            <Icon
              name="delete"
              size={isMobile ? theme.text.s6 : theme.text.s55}
              color={theme.text.error}
            />
          </ActionButton>
        </ActionContainer>
      </HeaderContainer>

      <InfoSection isMobile={isMobile}>
        <InfoLabel>Patient Name</InfoLabel>
        <InfoValue>{data?.patient?.name}</InfoValue>
      </InfoSection>

      <InfoSection isMobile={isMobile}>
        <InfoLabel>Date</InfoLabel>
        <InfoValue>{dayjs(data?.date).format('YYYY-MM-DD')}</InfoValue>
      </InfoSection>

      {/*{data?.status && (*/}
      {/*  <InfoSection>*/}
      {/*    <InfoLabel>Status</InfoLabel>*/}
      {/*    <InfoValue>{data?.status}</InfoValue>*/}
      {/*  </InfoSection>*/}
      {/*)}*/}

      {data?.notes && (
        <InfoSection isMobile={isMobile}>
          <InfoLabel>Notes</InfoLabel>
          <InfoValue>{data?.notes}</InfoValue>
        </InfoSection>
      )}
    </ReportCardContainer>
  );

  // Render form fields by section
  const renderFormFields = () => {
    if (!data?.form_fields?.length) return null;

    const groupedFields = data.form_fields.reduce(
      (acc, field) => {
        const section = field.form_field.section;
        if (!acc[section]) {
          acc[section] = [];
        }
        acc[section].push(field);
        return acc;
      },
      {} as Record<string, FormField[]>,
    );

    return (
      <>
        {Object.entries(groupedFields || {}).map(([section, fields]) => (
          <ReportCardContainer key={section}>
            <SectionTitle bold>{section}</SectionTitle>
            {fields.map(field => (
              <FieldContainer key={field.id}>
                <InfoLabel>{field.form_field.label}</InfoLabel>
                <InfoValue>
                  {field.form_field.type === 'multiple_choice'
                    ? getMultipleValueLabels(field)
                    : getValueLabel(field)}
                </InfoValue>
                {field.form_field.help_text && (
                  <TextGeneric
                    style={{
                      fontSize: theme.text.s6,
                      color: theme.text.light,
                      marginTop: 4,
                      fontStyle: 'italic',
                    }}>
                    {field.form_field.help_text}
                  </TextGeneric>
                )}
              </FieldContainer>
            ))}
          </ReportCardContainer>
        ))}
      </>
    );
  };

  return (
    <MainLayout
      enableScroll={true}
      title={tr('drawer.patients')}
      backHeader
      menuHeader
      noPadding>
      <Container>
        <ContentContainer>
          {isLoading ? (
            <ReportCardContainer>
              <TableRowSkeleton />
            </ReportCardContainer>
          ) : isError ? (
            <ReportCardContainer>
              <EmptyContainer>
                <EmptyList
                  iconName={'empty'}
                  iconColor={theme.primary}
                  iconSize={theme.text.s1 * 2}
                  title={'app.error'}
                  onRefreshPress={() => initPage()}
                  showRefresh={true}
                />
              </EmptyContainer>
            </ReportCardContainer>
          ) : (
            <>
              {renderPatientInfo()}
              {renderFormFields()}
            </>
          )}
        </ContentContainer>
      </Container>
    </MainLayout>
  );
};

ViewPatientReport.displayName = 'ViewPatientReportScreen';
export default ViewPatientReport;
